import React, { useEffect, useState } from 'react';

import { DownloadIcon } from '@vlabs/icons';
import {
  ChartForm, LISTS_GROUP_BY_VALUE, chartTypeOptions, chartConfigurations,
} from '@vlabs/monokit/entities/charts/chart-form';
import { useGetEventStats, useGetLists } from '@vlabs/api/luna-api/client';
import { routes } from '@vlabs/shared/navigation/routes';
import { downloadJson } from '@vlabs/shared/utils/files';
import {
  Panel, Divider, Control, Breadcrumbs, Breadcrumb,
} from '@vlabs/uikit';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useParams, generatePath } from 'react-router-dom';

import ChartPanel from './ChartPanel';
import st from './ChartSettings.module.sass';
import { groupByDataToFormValue } from './groupByDataToFormValue';
import { normalizeGroupByTargets, processListsGroupByChartData } from './groupByHelpers';
import { prepareStatisticFilters } from './prepareStatisticFilters';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';
import classNames from 'classnames';

export const downloadCSV = (fileName, data) => {
  const headers = data.fields.map((field) => field.split(',')[0].trim());

  let csvContent = `${headers.join(',')}\n`;
  data.stats.forEach((row) => {
    csvContent += `${row.map((value) => (value === null ? 'null' : value)).join(',')}\n`;
  });

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  FileSaver.saveAs(blob, `${fileName}.csv`);
};

export function ChartSettings() {
  const { t } = useTranslation();
  const { diagramType: statisticType } = useParams();
  const defaultValues = chartConfigurations[statisticType];

  const [chartData, setChartData] = useState(null);
  const [formData, setFormData] = useState(defaultValues);

  const getEventStats = useGetEventStats();
  const getLists = useGetLists({ page_size: 100 }, { enabled: false });

  const { diagramType } = formData?.getTemplateStats ?? defaultValues.getTemplateStats.diagramType;

  const getListsById = async () => {
    const { data } = await getLists.refetch();

    return data?.lists?.reduce((acc, list) => {
      acc[list.list_id] = list;
      return acc;
    }, {});
  };

  const fetchChartData = async (formValues) => {
    const hasListsGrouping = formValues.targets
      ?.some((target) => target?.column === LISTS_GROUP_BY_VALUE);

    // При выборе в Базовых параметрах "Списки лиц": на бек необходимо отправлять 'top_matching_candidates_label'
    const targets = structuredClone(formValues.targets);
    if (targets[0].column === 'lists') {
      targets[0].column = 'top_matching_candidates_label';
    }

    const response = await getEventStats.mutateAsync({
      data: {
        targets: [...normalizeGroupByTargets(formValues.targets)],
        group_by: groupByDataToFormValue(formValues.group_by),
        period: formValues.period,
        filters: prepareStatisticFilters(formValues.filters),
      },
    });

    let responseFields = [...(response?.fields ?? [])];

    // При выборе в Базовых параметрах "Списки лиц":
    // подменяем в ответе обратно 'top_matching_candidates_label' на 'lists' для корректного отображения заголовка графика
    if (formValues.targets[0].column === 'lists') {
      responseFields = responseFields.map((field) => {
        if (field.startsWith('top_matching_candidates_label')) {
          const fieldArr = field.split(',');
          fieldArr[0] = 'lists';
          return fieldArr.join(',');
        }
        return field;
      });
    }
    let listsById = {};

    if (hasListsGrouping) {
      try {
        listsById = await getListsById();
      } catch {
        listsById = {};
      }
    }

    setChartData(processListsGroupByChartData({
      chartData: {
        ...response,
        fields: responseFields,
      },
      groupByTargets: formValues.groupByTargets,
      listsById,
    }));
  };

  useEffect(() => {
    fetchChartData(defaultValues.getEventStats);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb
        caption={t('statistic:titles.page')}
        href={generatePath(routes.events.statistic.all)}
      />
      <Breadcrumb caption={chartTypeOptions?.[statisticType]?.label} />
    </Breadcrumbs>
  );

  return (
    <Page className={st.Page}>
      <PageContent>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={chartTypeOptions?.[statisticType]?.label}
      />
      <div className={st.ContentWrapper}>
        <Panel className={classNames(st.SettingsPanel, 'PanelAutoHeight')}>
          <ChartForm
            onChangeFormData={setFormData}
            onSubmit={fetchChartData}
            statisticType={statisticType}
          />
        </Panel>
        <Panel className={classNames(st.SettingsPanel, 'PanelAutoHeight')}>
          <div className={st.ChartPanelButtonGroup}>
            <Control.Button
              data-testid="downloadJsonButton"
              icon={<DownloadIcon />}
              kind="secondary"
              onClick={() => downloadJson(`statistic_${statisticType}`, chartData)}
            >
              JSON
            </Control.Button>
            <Divider small />
            <Control.Button
              data-testid="downloadCsvButton"
              icon={<DownloadIcon />}
              kind="secondary"
              onClick={() => downloadCSV(`statistic_${statisticType}`, chartData)}
            >
              CSV
            </Control.Button>
          </div>
          <ChartPanel
            chartData={chartData}
            chartType={diagramType}
          />
        </Panel>
      </div>
      </PageContent>
    </Page>
  );
}
